import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SupplyClass } from '@wodi/domain';
import type { ItemPharmacySettingsView } from '@wodi/shared';
import {
  Button,
  CardGrid,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  RadioGroup,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { itemPharmacyQuery, pharmacyKeys, setBarcodes, setSupplyClass } from '../api/pharmacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import pharmacy from '../pharmacy/pharmacy.module.css';
import { SUPPLY_CLASS_DESCRIPTIONS, SUPPLY_CLASS_LABELS } from '../pharmacy/supply-class.ts';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';
import { SupplyClassText } from './settings-pharmacy-items.tsx';

// One pharmacy item (docs/plans/phase1-dispensing.md §5.3; ADR 0014): its supply class,
// changed only with a reason (audited), and the barcodes that find it at the counter, each
// for the base unit or one of its packs. A barcode belongs to one item in the facility.

const BASE_UNIT = 'base';
/** The format the API and the database accept (stock_item_barcodes_format). */
const BARCODE_FORMAT = /^[0-9A-Za-z-]{4,64}$/;
const MAX_BARCODES = 20;
const MIN_REASON = 3;

type Barcode = ItemPharmacySettingsView['barcodes'][number];

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function PharmacyItemPage({ itemId }: { itemId: string }) {
  const item = useQuery(itemPharmacyQuery(itemId));
  const back = <Link to="/settings/pharmacy-items">All pharmacy items</Link>;
  if (item.isPending) {
    return (
      <SettingsPage title="Pharmacy item" intro={false} back={back} panel>
        <LoadingBlock label="Loading the item" rows={5} />
      </SettingsPage>
    );
  }
  if (item.isError) {
    const missing = item.error instanceof ApiError && item.error.status === 404;
    return (
      <SettingsPage title="Pharmacy item" intro={false} back={back} panel>
        <Notice
          tone="error"
          {...(missing
            ? {}
            : {
                action: (
                  <Button dense onPress={() => void item.refetch()}>
                    Try again
                  </Button>
                ),
              })}
        >
          {missing
            ? 'That item has no stock details, or it is not in this facility. Go back to the list and choose another.'
            : message(item.error, "The item couldn't be loaded. Try again.")}
        </Notice>
      </SettingsPage>
    );
  }
  const data = item.data;
  return (
    <SettingsPage title={data.name} intro={false} back={back}>
      <p className={pharmacy.meta}>
        <span className="wodi-id">{data.code}</span>
        {` · counted in ${data.baseUnitDisplay}`}
        {data.status === 'active' ? null : ' · not in use'}
      </p>
      <CardGrid>
        <SupplyClassSection key={`class-${data.supplyClass}`} item={data} />
        <BarcodesSection item={data} />
      </CardGrid>
    </SettingsPage>
  );
}

/** After a save: the item's own query and every list that shows it. */
function useSaved(itemId: string) {
  const queryClient = useQueryClient();
  return (next: ItemPharmacySettingsView) => {
    queryClient.setQueryData(pharmacyKeys.item(itemId), next);
    void queryClient.invalidateQueries({ queryKey: ['pharmacy', 'item-settings'] });
  };
}

function SupplyClassSection({ item }: { item: ItemPharmacySettingsView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const saved = useSaved(item.serviceItemId);
  const mayChange = session !== null && can(session, 'pharmacy.supply_class.manage');
  const [supplyClass, setClass] = useState<SupplyClass>(item.supplyClass);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () => setSupplyClass(item.serviceItemId, { supplyClass, reason: reason.trim() }),
    onSuccess: (next) => {
      saved(next);
      toasts.show({
        title: 'Supply class changed',
        description: `${next.name}: ${SUPPLY_CLASS_LABELS[next.supplyClass]}`,
      });
    },
  });
  const changed = supplyClass !== item.supplyClass;
  const reasonShort = reason.trim().length < MIN_REASON;
  const go = () => {
    setTried(true);
    if (!changed || reasonShort || !online || save.isPending) return;
    save.mutate();
  };
  useShortcut('mod+enter', go, {
    allowInFields: true,
    enabled: mayChange,
    description: 'Change the supply class',
  });
  return (
    <Section title="Supply class" description={SUPPLY_CLASS_DESCRIPTIONS[item.supplyClass]}>
      <p>
        <SupplyClassText supplyClass={item.supplyClass} isControlled={item.isControlled} />
      </p>
      {item.isControlled ? (
        <p className={pharmacy.meta}>
          Controlled medicines are never sold at the counter, whatever their supply class.
        </p>
      ) : null}
      {mayChange ? (
        <form
          className={pharmacy.form}
          aria-label="Change the supply class"
          onSubmit={(event) => {
            event.preventDefault();
            go();
          }}
        >
          <RadioGroup
            label="Supply class"
            value={supplyClass}
            onChange={(value) => {
              setClass(value as SupplyClass);
            }}
            options={(['prescription_only', 'over_the_counter'] as const).map((id) => ({
              id,
              label: SUPPLY_CLASS_LABELS[id],
              description: SUPPLY_CLASS_DESCRIPTIONS[id],
            }))}
          />
          {changed ? (
            <TextField
              label="Reason for the change"
              description="Kept in the audit log with your name."
              value={reason}
              onChange={setReason}
              {...(tried && reasonShort ? { errorMessage: 'Say why, in a few words.' } : {})}
            />
          ) : null}
          {save.error === null ? null : (
            <Notice tone="error">
              {message(save.error, "The supply class wasn't changed. Try again.")}
            </Notice>
          )}
          {online ? null : (
            <Notice tone="info">Changing the supply class needs a connection to Wodi.</Notice>
          )}
          <div className={pharmacy.actions}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!changed || !online}
              isPending={save.isPending}
              pendingLabel="Changing…"
            >
              Change supply class
            </Button>
            {changed ? <ShortcutHint combo="mod+enter" action="change it" /> : null}
          </div>
        </form>
      ) : (
        <p className={pharmacy.meta}>Only the pharmacist in charge can change the supply class.</p>
      )}
    </Section>
  );
}

function BarcodesSection({ item }: { item: ItemPharmacySettingsView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const saved = useSaved(item.serviceItemId);
  const mayChange = session !== null && can(session, 'stock.manage');
  const [text, setText] = useState('');
  const [pack, setPack] = useState<string>(BASE_UNIT);
  const [tried, setTried] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  useShortcut(
    'b',
    () => {
      fieldRef.current?.querySelector('input')?.focus();
    },
    { enabled: mayChange, description: 'Add a barcode' },
  );

  const packLabel = (code: string | null) => {
    if (code === null) return `One ${item.baseUnitDisplay}`;
    const found = item.packUnits.find((p) => p.code === code);
    return found === undefined
      ? code
      : `${found.display} (${String(found.baseUnitsPerPack)} ${item.baseUnitDisplay})`;
  };
  const packOptions = [
    { id: BASE_UNIT, label: packLabel(null) },
    ...item.packUnits.map((p) => ({ id: p.code, label: packLabel(p.code) })),
  ];

  const save = useMutation({
    mutationFn: (next: { barcodes: Barcode[]; done: string }) =>
      setBarcodes(item.serviceItemId, { barcodes: next.barcodes }),
    onSuccess: (next, { done }) => {
      saved(next);
      toasts.show({ title: done });
    },
  });

  const code = text.trim();
  const problem =
    code === ''
      ? 'Scan or type the barcode.'
      : !BARCODE_FORMAT.test(code)
        ? 'Use 4 to 64 letters, digits or dashes.'
        : item.barcodes.some((b) => b.barcode === code)
          ? 'This item already has that barcode.'
          : null;
  const full = item.barcodes.length >= MAX_BARCODES;
  const add = () => {
    setTried(true);
    if (problem !== null || full || !online || save.isPending) return;
    save.mutate(
      {
        barcodes: [
          ...item.barcodes,
          { barcode: code, packUnitCode: pack === BASE_UNIT ? null : pack },
        ],
        done: 'Barcode added',
      },
      {
        onSuccess: () => {
          setText('');
          setTried(false);
          fieldRef.current?.querySelector('input')?.focus();
        },
      },
    );
  };
  const remove = (barcode: string) => {
    save.mutate({
      barcodes: item.barcodes.filter((b) => b.barcode !== barcode),
      done: 'Barcode removed',
    });
  };

  return (
    <Section
      title="Barcodes"
      description="Scanning one of these at the counter or in dispensing finds this item, and the pack it names."
    >
      <Table flat aria-label="Barcodes">
        <TableHeader>
          <Column isRowHeader>Barcode</Column>
          <Column>Pack</Column>
          <Column>Actions</Column>
        </TableHeader>
        <TableBody
          items={item.barcodes.map((b) => ({ ...b, id: b.barcode }))}
          renderEmptyState={() =>
            mayChange ? 'No barcodes yet. Scan one into the field below.' : 'No barcodes yet.'
          }
        >
          {(b) => (
            <Row id={b.barcode}>
              <Cell>
                <span className="wodi-id">{b.barcode}</span>
              </Cell>
              <Cell>{packLabel(b.packUnitCode)}</Cell>
              <Cell>
                {mayChange ? (
                  <Button
                    variant="quiet"
                    dense
                    isDisabled={!online || save.isPending}
                    aria-label={`Remove barcode ${b.barcode}`}
                    onPress={() => {
                      remove(b.barcode);
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {mayChange ? (
        <form
          className={pharmacy.form}
          aria-label="Add a barcode"
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <div className={pharmacy.row}>
            <div ref={fieldRef}>
              <TextField
                label="Barcode"
                description="Scan the pack, or type the number under the bars. Press B to jump here."
                value={text}
                onChange={(value) => {
                  setText(value);
                  save.reset();
                }}
                {...(tried && problem !== null ? { errorMessage: problem } : {})}
              />
            </div>
            <Select
              label="Pack it names"
              options={packOptions}
              value={pack}
              onChange={(value) => {
                setPack(String(value));
              }}
            />
          </div>
          {save.error === null ? null : (
            <Notice tone="error">
              {message(save.error, "The barcodes weren't saved. Try again.")}
            </Notice>
          )}
          {full ? (
            <Notice tone="info">
              An item holds at most {MAX_BARCODES} barcodes. Remove one to add another.
            </Notice>
          ) : null}
          {online ? null : (
            <Notice tone="info">Changing barcodes needs a connection to Wodi.</Notice>
          )}
          <div className={pharmacy.actions}>
            <Button
              type="submit"
              isDisabled={!online || full}
              isPending={save.isPending}
              pendingLabel="Saving…"
            >
              Add barcode
            </Button>
          </div>
        </form>
      ) : (
        <p className={pharmacy.meta}>Only someone who manages stock can change barcodes.</p>
      )}
    </Section>
  );
}
